import {Choice, Label, NumberInput, Segment, Switch} from 'platform/components';
import {Box, Grid, gridMinmax, gridTracks, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import {NumericBoundBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import type {TestIdProps} from 'shared/web';

import {
  getNumericBoundMagnitudeInvalidEnds,
  mergeNumericBoundFieldError,
  type NumericBoundFieldErrorState,
} from '../utils/mergeNumericBoundFieldError';

interface UnitLabels {
  abs: string;
  pct: string;
}

interface BoundaryRowProps extends TestIdProps {
  label: string;
  /** null = disabled (all bounds off) */
  value: NumericBoundBody | null;
  onChange: (next: NumericBoundBody | null) => void;
  /** When present, shows an abs/pct segment toggle. The parent owns which side is active. */
  unitLabels?: UnitLabels;
  unit?: 'abs' | 'pct';
  onUnitChange?: (u: 'abs' | 'pct') => void;
  isReadOnly?: boolean;
  /** Pass `fieldState.error` from react-hook-form; visible text is derived with `mergeNumericBoundFieldError`. */
  errorMessage?: NumericBoundFieldErrorState;
}

/** Fixed-width label, flexible dropdown, input capped at 24 steps and a content-sized unit. */
const BOUNDARY_COLUMNS = gridTracks(8, gridMinmax(0, '1fr'), gridMinmax(0, 24), 'auto');

type EndMode = 'any' | 'same' | 'offset';

/** Derive UI dropdown state from a signed offset stored on the BE. */
function endModeFromValue(v: number | null): EndMode {
  if (isNil(v)) {
    return 'any';
  }
  if (v === 0) {
    return 'same';
  }
  return 'offset';
}

/** Convert dropdown state + magnitude to a signed offset. */
function valueFromEndMode(
  mode: EndMode,
  magnitude: number,
  direction: 'from' | 'to'
): number | null {
  if (mode === 'any') {
    return null;
  }
  if (mode === 'same') {
    return 0;
  }
  return direction === 'from' ? -Math.abs(magnitude) : Math.abs(magnitude);
}

function EndSelector({
  signedValue,
  onChange,
  direction,
  unitLabel,
  isReadOnly,
  isInvalid,
}: {
  signedValue: number | null;
  onChange: (next: number | null) => void;
  direction: 'from' | 'to';
  unitLabel: string;
  isReadOnly?: boolean;
  isInvalid?: boolean;
}) {
  const mode = endModeFromValue(signedValue);
  const magnitude = isNotNil(signedValue) ? Math.abs(signedValue) : 0;

  const label =
    direction === 'from'
      ? i18n.t('entity.sourcing.relativeFilters.bound.from')
      : i18n.t('entity.sourcing.relativeFilters.bound.to');

  const offsetTranslationKey =
    direction === 'from'
      ? i18n.t('entity.sourcing.relativeFilters.bound.offsetMinus')
      : i18n.t('entity.sourcing.relativeFilters.bound.offsetPlus');

  const offsetLabel = unitLabel ? `${offsetTranslationKey} ${unitLabel}` : offsetTranslationKey;

  const options: {value: EndMode; label: string}[] = [
    {value: 'any', label: i18n.t('entity.sourcing.relativeFilters.bound.any')},
    {
      value: 'same',
      label: i18n.t('entity.sourcing.relativeFilters.bound.same'),
    },
    {value: 'offset', label: offsetLabel},
  ];

  const handleModeChange = (next: EndMode | null) => {
    if (isNil(next)) {
      return;
    }
    const effectiveMagnitude = next === 'offset' ? Math.max(magnitude, 1) : magnitude;
    onChange(valueFromEndMode(next, effectiveMagnitude, direction));
  };

  const handleMagnitudeChange = (val: number | null) => {
    onChange(valueFromEndMode('offset', val ?? 0, direction));
  };

  const isOffset = mode === 'offset';

  return (
    <>
      <Text size="small" color="secondary">
        {label}
      </Text>
      <Choice<EndMode>
        value={mode}
        options={options}
        onChange={handleModeChange}
        isDisabled={isReadOnly}
        isNotClearable
      />
      <Box>
        <Show when={isOffset}>
          <NumberInput
            value={magnitude}
            onChange={handleMagnitudeChange}
            isDisabled={isReadOnly}
            decimalPlaces={0}
            isInvalid={!!isInvalid}
          />
        </Show>
      </Box>
      <Box>
        <Show when={isOffset}>
          <Text size="small" color="secondary">
            {unitLabel}
          </Text>
        </Show>
      </Box>
    </>
  );
}

export function BoundaryRow(props: BoundaryRowProps) {
  const {
    label,
    value,
    onChange,
    unitLabels,
    unit = 'abs',
    onUnitChange,
    isReadOnly,
    errorMessage: fieldError,
  } = props;
  const isEnabled = isNotNil(value);
  const unitLabel = unit === 'pct' ? '%' : (unitLabels?.abs ?? '');

  const {isFromMagnitudeInvalid, isToMagnitudeInvalid} =
    getNumericBoundMagnitudeInvalidEnds(fieldError);
  const mergedErrorText = mergeNumericBoundFieldError(fieldError);
  const rowErrorLines = mergedErrorText?.split('\n').filter((line) => isNotEmpty(trim(line))) ?? [];
  const hasRowErrorMessage = rowErrorLines.length > 0;

  const handleToggle = (enabled: boolean) => {
    onChange(enabled ? {from: null, to: null} : null);
  };

  const updateFrom = (from: number | null) => {
    onChange({...(value ?? {from: null, to: null}), from});
  };

  const updateTo = (to: number | null) => {
    onChange({...(value ?? {from: null, to: null}), to});
  };

  const segmentOptions = unitLabels
    ? [
        {value: 'abs' as const, label: unitLabels.abs},
        {value: 'pct' as const, label: '%'},
      ]
    : undefined;

  return (
    <VStack spacing={2} data-testid={props['data-testid']}>
      <HStack align="center" justify="space-between" spacing={2}>
        <HStack align="center" spacing={2}>
          <Switch value={isEnabled} onChange={handleToggle} isDisabled={isReadOnly} />
          <Label>{label}</Label>
        </HStack>
        <Show when={isEnabled && !!unitLabels && !!onUnitChange && !!segmentOptions}>
          <Segment<'abs' | 'pct'>
            value={unit}
            options={segmentOptions}
            onChange={(u) => onUnitChange?.(u)}
            isDisabled={isReadOnly}
          />
        </Show>
      </HStack>
      <Show when={isEnabled}>
        <Grid columns={BOUNDARY_COLUMNS} spacing={2} align="center">
          <EndSelector
            signedValue={value?.from ?? null}
            onChange={updateFrom}
            direction="from"
            unitLabel={unitLabel}
            isReadOnly={isReadOnly}
            isInvalid={isFromMagnitudeInvalid}
          />
          <EndSelector
            signedValue={value?.to ?? null}
            onChange={updateTo}
            direction="to"
            unitLabel={unitLabel}
            isReadOnly={isReadOnly}
            isInvalid={isToMagnitudeInvalid}
          />
        </Grid>
      </Show>
      <Show when={hasRowErrorMessage}>
        <VStack spacing={1}>
          {rowErrorLines.map((line) => (
            <Text key={line} size="xSmall" color="danger">
              {line}
            </Text>
          ))}
        </VStack>
      </Show>
    </VStack>
  );
}
